// layout/UserLayout.tsx  ← Keep this name if you want
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { PageLoader } from "../components";
import { getMeUser } from "../Services_V2/Auth.service";
import { LocalStorageItems, AppRoutes } from "../constants";

const UserLayout = ({ children }: { children: React.ReactNode }) => {
  const { state, dispatch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  const token = localStorage.getItem(LocalStorageItems.TOKEN);

  const isMyDonationRoute = useMemo(() => {
    return location.pathname === AppRoutes.ORGANIZATION.MY_DONATION;
  }, [location.pathname]);

  const handleLoading = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    // If user already loaded → skip API call
    if (state.user) {
      setLoading(false);
      return;
    }

    try {
      const user = await getMeUser();
      dispatch({ type: "updateState", payload: { user } });
    } catch (error: any) {
      console.error("Failed to fetch user:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem(LocalStorageItems.TOKEN);
      } else {
        setError("Failed to load user profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, state.user, dispatch]);

  useEffect(() => {
    handleLoading();
  }, [handleLoading]);

  if (!token) {
    return (
      <Navigate
        to={
          isMyDonationRoute
            ? AppRoutes.DONOR.DONOR_LOGIN
            : AppRoutes.ORGANIZATION.LOGIN
        }
        state={{ from: location }}
        replace
      />
    );
  }

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
        <button
          onClick={() => {
            localStorage.removeItem(LocalStorageItems.TOKEN);
            window.location.href = AppRoutes.ORGANIZATION.LOGIN;
          }}
          style={{ marginTop: "10px" }}
        >
          Logout
        </button>
      </div>
    );
  }

  if (!state.user) {
    return <Navigate to={AppRoutes.ORGANIZATION.LOGIN} />;
  }

  return <>{children}</>;
};

export default UserLayout;
